import React, { useState } from "react";
import { Button } from "../Components/UI/Button";
import { useQueryBuilder } from "../hooks/useQueryBuilder";
import { useSalesforce } from "../hooks/useSalesforce";
import {
  IncidentRecord,
  GroupedData,
  IncidentQueryField,
} from "../types/salesforce";
import GroupedResults from "./GroupedResults";
import { ChartVisualizer } from "./ChartVisualizer";
import {
  getPeriodStart,
  formatPeriod,
  fillMissingPeriods,
  DateGranularity,
} from "../utils/dateGrouping";
import { parseISO } from "date-fns";
import QueryModal from "./QueryModal";

const formatDateValue = (
  date: string,
  dateGranularity: DateGranularity
): string => {
  const parsedDate = parseISO(date || "");
  if (!isNaN(parsedDate.getTime())) {
    const periodStart = getPeriodStart(parsedDate, dateGranularity);
    return formatPeriod(periodStart, dateGranularity);
  }
  return "Undefined";
};

const getFieldValue = (
  record: IncidentRecord,
  fieldName: string,
  isDateField: boolean,
  dateGranularity?: DateGranularity
): string => {
  if (isDateField && dateGranularity) {
    return formatDateValue(record[fieldName], dateGranularity);
  }
  return String(record[fieldName] || "Undefined");
};

const isDateFieldType = (field?: IncidentQueryField): boolean => {
  return field?.Field_Type__c === "Date" || field?.Field_Type__c === "DateTime";
};

const processSubgroups = (
  record: IncidentRecord,
  level2Field: any,
  field2: IncidentQueryField | undefined,
  grouped: { [key: string]: any },
  value1: string
) => {
  const isDateField2 = isDateFieldType(field2);
  const value2 = getFieldValue(
    record,
    level2Field.fieldName,
    isDateField2,
    level2Field.dateGranularity
  );

  if (!grouped[value1].subgroups[value2]) {
    grouped[value1].subgroups[value2] = { records: [] };
  }
  grouped[value1].subgroups[value2].records.push(record);
};

const convertGroupedToArray = (
  grouped: { [key: string]: any },
  level2Field: any
): GroupedData[] => {
  return Object.entries(grouped).map(
    ([groupValue, groupData]: [string, any]) => {
      if (level2Field) {
        const subgroups = Object.entries(groupData.subgroups).map(
          ([subGroupValue, subGroupData]: [string, any]) => ({
            groupValue: subGroupValue,
            count: subGroupData.records.length,
            records: subGroupData.records,
          })
        );

        return {
          groupValue,
          count: subgroups.reduce((sum: number, sg: any) => sum + sg.count, 0),
          subgroups,
        };
      }

      return {
        groupValue,
        count: groupData.records.length,
        records: groupData.records,
      };
    }
  );
};

interface QueryExecutorProps {
  availableFields: IncidentQueryField[];
  onSubscribeClicked: any;
}

export const QueryExecutor: React.FC<QueryExecutorProps> = ({
  availableFields,
  onSubscribeClicked,
}) => {
  const {
    setQuery,
    facilityId,
    selectedFields,
    filters,
    logicExpression,
    orderBy,
    groupBy,
  } = useQueryBuilder();

  const compSelectedFields = React.useMemo(() => {
    return selectedFields.map((f) => f.Field_API_Name__c);
  }, [selectedFields]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { queryIncidents, isLoading, error } = useSalesforce();
  const [results, setResults] = useState<IncidentRecord[]>([]);
  const [groupedResults, setGroupedResults] = useState<GroupedData[]>([]);
  const [queryError, setQueryError] = useState<string | null>(null);

  const groupData = (data: IncidentRecord[]) => {
    if (!groupBy.length) return [];

    const level1Field = groupBy.find((g) => g.level === 1);
    const level2Field = groupBy.find((g) => g.level === 2);

    if (!level1Field) return [];

    const field1 = availableFields.find(
      (f) => f.Field_API_Name__c === level1Field.fieldName
    );
    const isDateField1 = isDateFieldType(field1);
    const grouped: { [key: string]: any } = {};

    // First level grouping
    data.forEach((record) => {
      const value1 = getFieldValue(
        record,
        level1Field.fieldName,
        isDateField1,
        level1Field.dateGranularity
      );

      if (!grouped[value1]) {
        grouped[value1] = {
          records: [],
          subgroups: level2Field ? {} : undefined,
        };
      }

      if (level2Field) {
        const field2 = availableFields.find(
          (f) => f.Field_API_Name__c === level2Field.fieldName
        );
        processSubgroups(record, level2Field, field2, grouped, value1);
      } else {
        grouped[value1].records.push(record);
      }
    });

    let result = convertGroupedToArray(grouped, level2Field);

    // Fill in missing periods for date fields
    if (isDateField1 && level1Field.dateGranularity) {
      const groupedMap = new Map(result.map((g) => [g.groupValue, g.count]));
      const filledPeriods = fillMissingPeriods(
        groupedMap,
        level1Field.fieldName,
        level1Field.dateGranularity,
        data
      );

      result = filledPeriods.map((period) => ({
        groupValue: period.displayValue,
        count: period.count,
        records: grouped[period.displayValue]?.records || [],
      }));
    }

    return result;
  };

  const handleExecuteQuery = async () => {
    if (facilityId.length === 0) {
      setQueryError("Facility is required");
      return;
    }

    if (!orderBy) {
      setQueryError("Order by is required");
      return;
    }

    try {
      setQueryError(null);
      const data = await queryIncidents(
        facilityId,
        compSelectedFields,
        filters,
        logicExpression,
        orderBy,
        setQuery
      );
      setResults(data);

      if (groupBy.length > 0) {
        const grouped = groupData(data);
        setGroupedResults(grouped);
      } else {
        setGroupedResults([]);
      }

      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to execute query:", err);
      setQueryError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    }
  };

  const renderTableHeader = () => (
    <tr>
      {compSelectedFields.map((field) => (
        <th
          key={field}
          className="cursor-pointer border-b border-purple-100/30 p-3 text-left text-sm font-medium text-purple-900"
        >
          {availableFields.find((f) => f.Field_API_Name__c === field)?.Label ||
            field}
        </th>
      ))}
    </tr>
  );

  const renderTableRow = (record: IncidentRecord) => (
    <tr key={record.Id || `${Object.values(record).join("-")}`}>
      {compSelectedFields.map((field) => (
        <td
          key={`${record.Id || Object.values(record).join("-")}-${field}`}
          className="border-b border-purple-100/30 p-3 text-sm text-gray-700"
        >
          {String(record[field]) !== "undefined" ? String(record[field]) : "-"}
        </td>
      ))}
    </tr>
  );

  const renderResults = () => {
    if (groupBy.length > 0 && groupedResults.length > 0) {
      return (
        <div>
          <div className="mb-6">
            <GroupedResults
              groupedData={groupedResults}
              selectedFields={compSelectedFields}
              availableFields={availableFields}
              level1Field={groupBy.find((g) => g.level === 1)!.fieldName}
              level2Field={groupBy.find((g) => g.level === 2)?.fieldName}
            />
          </div>
          <ChartVisualizer
            groupedData={groupedResults}
            availableFields={availableFields}
            level1Field={groupBy.find((g) => g.level === 1)!.fieldName}
            level2Field={groupBy.find((g) => g.level === 2)?.fieldName}
          />
        </div>
      );
    }

    if (results?.length > 0) {
      return (
        <div>
          <h3 className="text-lg font-semibold">Query Results:</h3>
          <div className="overflow-hidden rounded-lg border border-purple-100/30">
            <table className="w-full border-collapse">
              <thead>{renderTableHeader()}</thead>
              <tbody className="divide-y divide-purple-100/30">
                {results.map(renderTableRow)}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="flex justify-end">
        <Button
          className="w-full cursor-pointer border-0 bg-purple-400 text-white-main hover:bg-purple-500 md:w-auto"
          disabled={isLoading}
          onClick={handleExecuteQuery}
        >
          {isLoading ? "Generating Report..." : "Generate Report"}
        </Button>
      </div>

      {queryError && (
        <div className="rounded-lg bg-red-50 p-4 text-red-500">
          Error: {queryError}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-red-500">
          API Error: {error}
        </div>
      )}

      <QueryModal
        onSubscribeClicked={onSubscribeClicked}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <div className="space-y-6">{renderResults()}</div>
      </QueryModal>
    </div>
  );
};

export default QueryExecutor;
